/** Pure helpers for the Redstone Relay play screen (tap rules, hints, next level, square size). */
import {
  DIRECTIONAL,
  type Board,
  type Dir,
  type PieceKind,
  type RedstoneLevel,
  type TickState,
} from './redstone.ts';
import type { Medal } from './rewards.ts';

export type Tool = PieceKind | 'remove';

/** What a tap does with the selected tool; null when nothing changes. Never mutates `board`. */
export function applyTap(
  level: RedstoneLevel,
  board: Board,
  x: number,
  y: number,
  tool: Tool,
): Board | null {
  const fixed = level.fixed[`${x},${y}`];
  if (fixed?.kind === 'lever') {
    return { ...board, levers: { ...board.levers, [fixed.id]: !board.levers[fixed.id] } };
  }
  if (fixed) return null;
  const here = board.placements.find((p) => p.x === x && p.y === y);
  if (here) {
    if (tool === 'remove' || !DIRECTIONAL.has(here.piece))
      return { ...board, placements: board.placements.filter((p) => p !== here) };
    const dir = ((here.dir + 1) % 4) as Dir;
    return { ...board, placements: board.placements.map((p) => (p === here ? { ...p, dir } : p)) };
  }
  if (tool === 'remove') return null;
  const used = board.placements.filter((p) => p.piece === tool).length;
  if ((level.supply[tool] ?? 0) - used <= 0) return null;
  return { ...board, placements: [...board.placements, { x, y, piece: tool, dir: 1 }] };
}

export interface WrongLamp {
  x: number;
  y: number;
  want: 'lit' | 'dark';
}

/** Lamps whose state in `tick` differs from their goal, sorted by y then x. */
export function wrongLamps(level: RedstoneLevel, tick: TickState): WrongLamp[] {
  const out: WrongLamp[] = [];
  for (const [k, c] of Object.entries(level.fixed)) {
    if (c.kind !== 'lamp' || tick.lit.includes(k) === c.goal) continue;
    const [x, y] = k.split(',').map(Number) as [number, number];
    out.push({ x, y, want: c.goal ? 'lit' : 'dark' });
  }
  return out.sort((a, b) => a.y - b.y || a.x - b.x);
}

/** First unsolved level; if all are solved, the first without gold; otherwise null. */
export function nextLevelId(
  levels: readonly { id: string }[],
  solved: Partial<Record<string, Medal>>,
): string | null {
  return (
    (levels.find((l) => !solved[l.id]) ?? levels.find((l) => solved[l.id] !== 'gold'))?.id ?? null
  );
}

export const cellSize = (width: number, available: number): number =>
  Math.min(64, Math.max(44, Math.floor(available / width)));
